import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api-client';
import { AuthContext } from './auth-context';
import type { AdminUser } from './types';

interface SessionResponse {
  admin: AdminUser;
}

/**
 * Session state for the admin portal.
 *
 * The browser never decides who is signed in. Session tokens live in httpOnly
 * cookies that script cannot read, so the only way to know whether there is a
 * session is to ask the API — which is what makes this an async check on mount
 * rather than a synchronous read of local storage.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const { admin } = await api.get<SessionResponse>('/admin/auth/me');
        if (!cancelled) setUser(admin);
      } catch {
        // Any failure means no usable session. The API client already tried a
        // refresh, so there is nothing further to attempt here.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const signInWithGoogleCredential = useCallback(async (idToken: string) => {
    // Errors propagate deliberately: the sign-in screen needs the API's code
    // and message to say *why* access was refused — wrong domain, not on the
    // allowlist, SSO unconfigured — rather than a generic failure.
    const { admin } = await api.post<SessionResponse>('/admin/auth/google', {
      idToken,
    });
    setUser(admin);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post<void>('/admin/auth/logout');
    } finally {
      // Clear locally even if the call failed. The cookies may be gone already,
      // and leaving the UI looking signed-in would be worse than a stale
      // server-side row that expires on its own.
      setUser(null);
      window.google?.accounts.id.disableAutoSelect();
    }
  }, []);

  const value = useMemo(
    () => ({ user, isLoading, signInWithGoogleCredential, signOut }),
    [user, isLoading, signInWithGoogleCredential, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
